import React, { useEffect, useState, useRef } from "react";
import { Modal, Form, Radio, Select, Input, Button, message, Spin } from "antd";
import { platformApi } from "../../../../api";
import CustomerDetailsAddForm from "./VehicleDetails/AddCustomer";

const { Item } = Form;
const { Option } = Select;

const ClaimIntimationModalComponent = ({
  open,
  onCancel,
  jobOrder,
  onSuccess,
  form,
}) => {
  const { getFieldDecorator, setFieldsValue, validateFields, resetFields } = form;
  const [loading, setLoading] = useState(false);
  const [insurers, setInsurers] = useState([]);
  const [surveyors, setSurveyors] = useState([]);
  const [claimType, setClaimType] = useState("Cashless");
  const [searchString, setSearchString] = useState("");
  const [addSurveyorModal, setAddSurveyorModal] = useState(false);
  const [cusId, setCusId] = useState("");
  const surveyorErrorShownRef = useRef(false);

  useEffect(() => {
    if (open && jobOrder?.id) {
      loadInsurers();
      loadSurveyors("");
      loadClaimIntimation();
    } else if (!open) {
      resetFields();
    }
  }, [open, jobOrder]);

  const loadInsurers = async () => {
    try {
      const res = await platformApi.get("/api/insurance");
      const list = res?.data?.response?.data || res?.data?.response || res?.data || [];
      if (Array.isArray(list)) {
        setInsurers(list);
      }
    } catch (err) {
      console.error("Failed to load insurers", err);
    }
  };

  const loadSurveyors = async (searchString = "") => {
    try {
      const payload = {
        module: "customers",
        column: "phone",
        fields: ["contacts{phone}"],
        size: 50,
        searchColumns: ["contacts"],
        searchString: searchString,
        conditions: [
          {
            column: "customerType",
            operator: "EQUALS",
            value: "Insurance Surveyor"
          }
        ]
      };
      const res = await platformApi.post("/api/options/get", payload);
      const list = res?.data?.response || [];
      if (Array.isArray(list)) {
        setSurveyors(list);
      }
    } catch (err) {
      console.error("Failed to load surveyors", err);
    }
  };

  const handleSurveyorSearch = (value) => {
    setSearchString(value);
    if (!value) {
      surveyorErrorShownRef.current = false;
      loadSurveyors("");
      return;
    }

    if (/\D/.test(value)) {
      if (!surveyorErrorShownRef.current) {
        message.error("Please enter Mobile No", 2);
        surveyorErrorShownRef.current = true;
      }
      setSurveyors([]);
      return;
    }

    surveyorErrorShownRef.current = false;
    loadSurveyors(value);
  };

  const loadClaimIntimation = async () => {
    setLoading(true);
    try {
      const res = await platformApi.get(`/api/claimIntimation/${jobOrder.id}`);
      const data = res?.data?.response?.data;
      if (data) {
        const loadedType = data.claimType || "Cashless";
        setClaimType(loadedType);
        setFieldsValue({
          claimType: loadedType,
          insurer: data.insurer || undefined,
          claimNumber: data.claimNumber || "",
          surveyor: data.surveyor || undefined,
        });
      } else {
        setClaimType("Cashless");
        setFieldsValue({
          claimType: "Cashless",
        });
      }
    } catch (err) {
      console.error("Error loading claim intimation", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    validateFields(async (err, values) => {
      if (err) return;
      setLoading(true);

      const hasClaimNum = Boolean(values.claimNumber && values.claimNumber.trim() !== "");
      const hasSurveyor = Boolean(
        values.surveyor !== undefined &&
        values.surveyor !== null &&
        values.surveyor !== ""
      );

      try {
        const payload = {
          jobOrder: jobOrder.id,
          claimType: values.claimType,
          insurer: values.insurer,
          claimNumber: hasClaimNum ? values.claimNumber.trim() : "",
          surveyor: hasSurveyor ? values.surveyor : null,
        };

        const res = await platformApi.post("/api/claimIntimation", payload);
        const code = res?.data?.code || res?.status;

        if (code === 200 || code === 201) {
          if (!hasClaimNum && !hasSurveyor) {
            message.success("Claim details saved. JobCard stays in Vehicle Received section.");
            if (onSuccess) onSuccess("1");
          } else {
            const targetStatus = hasSurveyor ? "Surveyor Appointed" : "Claim Intimated";
            message.success(`Claim details saved! JobCard moved to ${targetStatus}.`);
            if (onSuccess) onSuccess("2");
          }
          onCancel();
        } else {
          message.error(res?.data?.message || "Failed to save Claim Intimation");
        }
      } catch (error) {
        console.error("Error saving claim intimation", error);
        message.error("Failed to save Claim Intimation");
      } finally {
        setLoading(false);
      }
    });
  };

  const handleCreateSurveyor = async (customerData) => {
    try {
      setLoading(true);
      const res = await platformApi.post("/api/customer", customerData);
      if (res?.data?.code === 200 || res?.data?.code === 201) {
        message.success("Surveyor customer created successfully");
        setAddSurveyorModal(false);
        const newId = res.data.response?.data?.id || res.data.response?.id;
        const phone = customerData.contacts?.[0]?.phone || searchString;

        await loadSurveyors(phone);
        if (newId) {
          setFieldsValue({ surveyor: newId });
        }
      } else {
        message.error("Failed to create surveyor");
      }
    } catch (err) {
      console.error("Error creating surveyor:", err);
      message.error("Failed to create surveyor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Claim Intimation"
      visible={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      width={520}
    >
      <Spin spinning={loading}>
        <Form layout="vertical" onSubmit={handleSubmit}>
          <Item label="Company Name (Insurer)" style={{ marginBottom: "5px" }}>
            {getFieldDecorator("insurer", {
              rules: [{ required: true, message: "Please select insurance company" }],
            })(
              <Select
                placeholder="Select Insurance Company"
                showSearch
                optionFilterProp="children"
                onChange={(val) => {
                  const ins = insurers.find((i) => i.id === val);
                  if (ins) {
                    const cashlessVal = String(ins.cashless || "").toLowerCase().trim();
                    const isCashless =
                      cashlessVal === "yes" ||
                      cashlessVal === "true" ||
                      cashlessVal === "cashless";
                    const newType = isCashless ? "Cashless" : "Non Cashless";
                    setClaimType(newType);
                    setFieldsValue({ claimType: newType });
                  }
                }}
              >
                {insurers.map((ins) => (
                  <Option key={ins.id} value={ins.id}>
                    {ins.name}
                  </Option>
                ))}
              </Select>
            )}
          </Item>

          <Item label="Claim Type" style={{ marginBottom: "5px" }}>
            {getFieldDecorator("claimType", {
              rules: [{ required: true, message: "Please select claim type" }],
              initialValue: "Cashless",
            })(
              <Radio.Group style={{ marginTop: "6px" }} disabled>
                <Radio value="Cashless"><span style={{ color: "rgba(0, 0, 0, 0.65)" }}>Cashless</span></Radio>
                <Radio value="Non Cashless"><span style={{ color: "rgba(0, 0, 0, 0.65)" }}>Non Cashless</span></Radio>
              </Radio.Group>
            )}
          </Item>

          <Item label="Claim Number" style={{ marginBottom: "5px" }}>
            {getFieldDecorator("claimNumber", {
              initialValue: "",
            })(<Input placeholder="Enter Claim Number" />)}
          </Item>

          <Item label="Surveyor" style={{ marginBottom: "5px" }}>
            {getFieldDecorator("surveyor", {
              initialValue: undefined,
            })(
              <Select
                placeholder="Select Surveyor"
                showSearch
                filterOption={false}
                onSearch={handleSurveyorSearch}
                allowClear
                notFoundContent={
                  searchString && !/\D/.test(searchString) ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
                      <Button
                        style={{ backgroundColor: "#006666", color: "#fff", border: "none" }}
                        onClick={() => setAddSurveyorModal(true)}
                      >
                        Add Surveyor
                      </Button>
                    </div>
                  ) : (
                    "No Data"
                  )
                }
              >
                {surveyors.map((survivor) => {
                  const phone = survivor.contacts?.[0]?.phone || survivor.phone || "";
                  const labelText = phone ? `${survivor.name} - ${phone}` : survivor.name;
                  return (
                    <Option key={survivor.id} value={survivor.id}>
                      {labelText}
                    </Option>
                  );
                })}
              </Select>
            )}
          </Item>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              marginTop: "24px",
            }}
          >
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Save Claim Intimation
            </Button>
          </div>
        </Form>
      </Spin>
      {addSurveyorModal && (
        <CustomerDetailsAddForm
          open={addSurveyorModal}
          close={() => setAddSurveyorModal(false)}
          defaultCustomerType="Insurance Surveyor"
          contact={searchString}
          emitData={handleCreateSurveyor}
          editable={true}
          cusId={cusId}
          setCusId={setCusId}
        />
      )}
    </Modal>
  );
};

const ClaimIntimationModal = Form.create()(ClaimIntimationModalComponent);
export default ClaimIntimationModal;
